import {
  BODY_PART_OPTIONS,
  EQUIPMENT_OPTIONS,
  TRACKING_TYPE_VALUE_TO_LABEL,
} from './select-options';
import { isExercisePreferenceTrackingType } from '../types/domain/user-preferences.types';

import type { QueryParams } from '../types/api/query.types';
import type {
  CustomExercise,
  ExternalExercise,
} from '../types/domain/exercise.types';
import type { ExercisePreferenceTrackingType } from '../types/domain/user-preferences.types';
import type { TrackingType } from '../validation/common.schemas';

/**
 * Exercise Library Model
 *
 * The Exercise Library (`/exercises`): the user's own exercises on one tab and
 * the third-party catalog on the other, each searched, filtered and loaded a
 * page at a time. The rules are the same on web (`ExercisesPage`) and mobile
 * (`ExercisesScreen`) and live here as pure functions; each platform draws it
 * and loads the pages.
 */

// =============================================================================
// FILTER STATE
// =============================================================================

/**
 * The two tabs of the Exercise Library:
 * - `custom`: the user's owned library (loaded as soon as the page opens).
 * - `third-party`: the borrowed external catalog (asked only once searched).
 */
export type ExerciseLibraryTab = 'custom' | 'third-party';

/**
 * The library's filters: a flat, optional bag that becomes the list's query.
 *
 * Body part and equipment are multi-value (the backend accepts comma-separated
 * values, e.g. `equipment=barbell,dumbbell`), capped at
 * {@link MAX_FILTER_VALUES} selections per criterion in the filter UI.
 */
export interface ExerciseLibraryFilterState {
  /** Free-text query (name search). */
  search?: string;
  /** Body part / muscle group filter values (max {@link MAX_FILTER_VALUES}). */
  bodyPart?: string[];
  /** Equipment filter values (max {@link MAX_FILTER_VALUES}). */
  equipment?: string[];
  /**
   * Hide public (shared) exercises, leaving only the user's own private ones
   * (Custom tab only — sends `is_public=false` when set; unset shows all).
   */
  hidePublic?: boolean;
}

/** Maximum selected values per multi-value filter criterion. */
export const MAX_FILTER_VALUES = 3;

/** How long the search waits after the last keystroke before it asks. */
export const EXERCISE_LIBRARY_SEARCH_DEBOUNCE_MS = 350;

/** Minimum query length before the third-party catalog will fetch. */
export const EXERCISE_CATALOG_MIN_QUERY_LENGTH = 2;

/** The keys a filter UI reports changes under. */
export const EXERCISE_LIBRARY_FILTER_KEYS = {
  search: 'search',
  visibility: 'visibility',
  bodyPart: 'bodyPart',
  equipment: 'equipment',
} as const;

/** The two capped, multi-value criteria. */
export type ExerciseLibraryMultiFilterKey = 'bodyPart' | 'equipment';

/** The single option of the Visibility filter (on = hide public). */
export const HIDE_PUBLIC_OPTION = {
  value: 'hide-public',
  label: 'Hide public exercises',
} as const;

/** Chip label for the active hide-public filter (reads as state, not command). */
export const HIDE_PUBLIC_CHIP_LABEL = 'Hiding public';

// =============================================================================
// QUERY
// =============================================================================

/**
 * The query one page of either list is asked with (`search`, `body_part`,
 * `equipment`). Multi-value criteria are sent comma-separated
 * (`equipment=barbell,dumbbell`), which the backend ORs within the criterion.
 */
export function buildExerciseLibraryQuery(
  page: number,
  filters: ExerciseLibraryFilterState | undefined
): QueryParams {
  // No `per_page`: the API sets each catalog's page size itself.
  const params: QueryParams = { page };

  const search = filters?.search?.trim();
  if (search) {
    params.search = search;
  }
  if (filters?.bodyPart?.length) {
    params.body_part = filters.bodyPart.join(',');
  }
  if (filters?.equipment?.length) {
    params.equipment = filters.equipment.join(',');
  }
  if (filters?.hidePublic) {
    params.is_public = false;
  }

  return params;
}

/**
 * Whether the third-party catalog has something to ask for: a search of at
 * least {@link EXERCISE_CATALOG_MIN_QUERY_LENGTH} characters, or a body part
 * or equipment filter. Hiding public exercises is not a catalog filter.
 */
export function isExerciseCatalogQueryActive(
  filters: ExerciseLibraryFilterState
): boolean {
  const query = filters.search?.trim() ?? '';
  return (
    query.length >= EXERCISE_CATALOG_MIN_QUERY_LENGTH ||
    (filters.bodyPart?.length ?? 0) > 0 ||
    (filters.equipment?.length ?? 0) > 0
  );
}

/** Whether anything, the search included, is narrowing the list. */
export function hasExerciseLibraryFilters(
  filters: ExerciseLibraryFilterState
): boolean {
  return Boolean(
    filters.search ||
    filters.bodyPart?.length ||
    filters.equipment?.length ||
    filters.hidePublic
  );
}

/**
 * Active-filter count for a trigger badge: the search term counts once,
 * multi-value criteria count one per selected value.
 */
export function getActiveFilterCount(
  filters: ExerciseLibraryFilterState
): number {
  return (
    (filters.search ? 1 : 0) +
    (filters.bodyPart?.length ?? 0) +
    (filters.equipment?.length ?? 0) +
    (filters.hidePublic ? 1 : 0)
  );
}

// =============================================================================
// FILTER OPTIONS, CHANGES AND CHIPS
// =============================================================================

export interface ExerciseLibraryFilterOption {
  value: string;
  label: string;
  /** At the cap, the options not yet selected cannot be added. */
  disabled: boolean;
}

const MULTI_FILTER_OPTIONS: Record<
  ExerciseLibraryMultiFilterKey,
  readonly { value: string; label: string }[]
> = {
  bodyPart: BODY_PART_OPTIONS,
  equipment: EQUIPMENT_OPTIONS,
};

const MULTI_FILTER_LABELS: Record<
  ExerciseLibraryMultiFilterKey,
  Map<string, string>
> = {
  bodyPart: new Map(BODY_PART_OPTIONS.map((o) => [o.value, o.label])),
  equipment: new Map(EQUIPMENT_OPTIONS.map((o) => [o.value, o.label])),
};

function isMultiFilterKey(key: string): key is ExerciseLibraryMultiFilterKey {
  return key === 'bodyPart' || key === 'equipment';
}

/**
 * A criterion's options. At {@link MAX_FILTER_VALUES} the not-yet-selected
 * ones disable; selected ones stay enabled so they can be unticked.
 */
export function getExerciseLibraryFilterOptions(
  key: ExerciseLibraryMultiFilterKey,
  selected: readonly string[]
): ExerciseLibraryFilterOption[] {
  const atCap = selected.length >= MAX_FILTER_VALUES;
  return MULTI_FILTER_OPTIONS[key].map((option) => ({
    value: option.value,
    label: option.label,
    disabled: atCap && !selected.includes(option.value),
  }));
}

/**
 * The filter state after a filter UI reports a change under one of
 * {@link EXERCISE_LIBRARY_FILTER_KEYS}. An empty, missing or unrecognised
 * value clears that filter; a multi-value selection is cut at the cap.
 */
export function applyExerciseLibraryFilterChange(
  filters: ExerciseLibraryFilterState,
  key: string,
  value: unknown
): ExerciseLibraryFilterState {
  const next: ExerciseLibraryFilterState = { ...filters };

  if (key === EXERCISE_LIBRARY_FILTER_KEYS.search) {
    if (typeof value === 'string' && value !== '') {
      next.search = value;
    } else {
      delete next.search;
    }
  } else if (isMultiFilterKey(key)) {
    const selection = (Array.isArray(value) ? value : [])
      .filter((v): v is string => typeof v === 'string')
      .slice(0, MAX_FILTER_VALUES);
    if (selection.length > 0) {
      next[key] = selection;
    } else {
      delete next[key];
    }
  } else if (key === EXERCISE_LIBRARY_FILTER_KEYS.visibility) {
    // A toggle: re-selecting the active option reports an empty value.
    if (value === HIDE_PUBLIC_OPTION.value) {
      next.hidePublic = true;
    } else {
      delete next.hidePublic;
    }
  }

  return next;
}

export interface ExerciseLibraryChip {
  /** `bodyPart:<value>` / `equipment:<value>` / `visibility` / `search`. */
  key: string;
  label: string;
}

export interface ExerciseLibraryChipOptions {
  /** Draw the search as a chip. @default true */
  search?: boolean;
  /** Draw the hide-public filter as a chip. @default true */
  visibility?: boolean;
}

/**
 * The active filters as dismissible chips, one per selected value; the chip
 * key carries criterion and value, so a dismiss removes just that value.
 */
export function buildExerciseLibraryChips(
  filters: ExerciseLibraryFilterState,
  { search = true, visibility = true }: ExerciseLibraryChipOptions = {}
): ExerciseLibraryChip[] {
  const chips: ExerciseLibraryChip[] = [];

  for (const key of ['bodyPart', 'equipment'] as const) {
    for (const value of filters[key] ?? []) {
      chips.push({
        key: `${key}:${value}`,
        label: MULTI_FILTER_LABELS[key].get(value) ?? value,
      });
    }
  }
  if (visibility && filters.hidePublic) {
    chips.push({
      key: EXERCISE_LIBRARY_FILTER_KEYS.visibility,
      label: HIDE_PUBLIC_CHIP_LABEL,
    });
  }
  if (search && filters.search) {
    chips.push({
      key: EXERCISE_LIBRARY_FILTER_KEYS.search,
      label: `"${filters.search}"`,
    });
  }

  return chips;
}

/** The filter state after the chip with `chipKey` is dismissed. */
export function dismissExerciseLibraryChip(
  filters: ExerciseLibraryFilterState,
  chipKey: string
): ExerciseLibraryFilterState {
  if (
    chipKey === EXERCISE_LIBRARY_FILTER_KEYS.search ||
    chipKey === EXERCISE_LIBRARY_FILTER_KEYS.visibility
  ) {
    return applyExerciseLibraryFilterChange(filters, chipKey, undefined);
  }

  const separator = chipKey.indexOf(':');
  const key = chipKey.slice(0, separator);
  const value = chipKey.slice(separator + 1);
  if (separator < 0 || !isMultiFilterKey(key)) {
    return filters;
  }
  return applyExerciseLibraryFilterChange(
    filters,
    key,
    (filters[key] ?? []).filter((v) => v !== value)
  );
}

// =============================================================================
// CARDS
// =============================================================================

/**
 * A tracking type as its display label. A label (`Weight & Reps`, as
 * `CustomExercise` carries) is returned as-is; a value (`weight_reps`) is
 * mapped to its label.
 */
export function formatExerciseTrackingType(
  value: ExercisePreferenceTrackingType | TrackingType
): ExercisePreferenceTrackingType {
  return isExercisePreferenceTrackingType(value)
    ? value
    : TRACKING_TYPE_VALUE_TO_LABEL[value];
}

/** First glyph of the name, upper-cased — a card's identity anchor. */
export function getExerciseMonogram(name: string): string {
  return name.trim().charAt(0).toUpperCase() || '?';
}

export interface CustomExerciseCardModel {
  monogram: string;
  /** Body part, equipment and target — whichever the exercise has. */
  meta: string[];
  /** "Weight & Reps · Intermediate"; empty when the exercise has neither. */
  microLabel: string;
  isPublic: boolean;
  /**
   * Whether the exercise is the user's to edit or delete. A public (shared)
   * exercise is not, so neither action is ever offered for it.
   */
  canModify: boolean;
}

/** What a custom exercise's card shows. */
export function getCustomExerciseCardModel(
  exercise: CustomExercise
): CustomExerciseCardModel {
  const isPublic = Boolean(exercise.is_public);
  return {
    monogram: getExerciseMonogram(exercise.name),
    meta: [exercise.body_part, exercise.equipment, exercise.target].filter(
      (value): value is string => Boolean(value)
    ),
    microLabel: [
      exercise.tracking_type
        ? formatExerciseTrackingType(exercise.tracking_type)
        : null,
      exercise.difficulty_level,
    ]
      .filter(Boolean)
      .join(' · '),
    isPublic,
    canModify: !isPublic,
  };
}

/** A catalog exercise's meta: body part and equipment, whichever it has. */
export function getExternalExerciseMeta(exercise: ExternalExercise): string[] {
  return [exercise.bodyPart, exercise.equipment].filter(
    (value): value is string => Boolean(value)
  );
}

// =============================================================================
// LABELS
// =============================================================================

/**
 * "128 results · from external catalog". Counts the matches when the API says
 * how many there are, otherwise what has been loaded.
 */
export function getCatalogResultLabel(
  total: number | null | undefined,
  loadedCount: number
): string {
  const count = typeof total === 'number' ? total : loadedCount;
  return `${count} result${count === 1 ? '' : 's'} · from external catalog`;
}

/** `Search the catalog for "split squat"` — the offer under an empty search. */
export function getCatalogSearchOfferLabel(search: string): string {
  return `Search the catalog for “${search.trim()}”`;
}
